"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Link,
  InputAdornment,
  Alert,
  Collapse,
} from "@mui/material";
import { Visibility, VisibilityOff, Close as CloseIcon } from "@mui/icons-material";
import GlobalLogicLogo from "../components/common/GlobalLogicLogo";
import Loader from "../components/common/Loader";

import { LoginErrorData } from "../services/auth";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSwitcher from "../components/specific/LanguageSwitcher/LanguageSwitcher";

interface FormData {
  username: string;
  password: string;
  rememberPassword: boolean;
}

interface FormErrors {
  username?: string;
  password?: string;
}

const Login = () => {
  const { t } = useLanguage();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    username: "",
    password: "",
    rememberPassword: false,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const { login } = useAuth();

  const navigate = useNavigate();

  const validateForm = useCallback(() => {
    const newErrors: FormErrors = {};
    if (!formData.username) {
      newErrors.username = t("login.errors.usernameEmpty");
    }
    if (!formData.password) {
      newErrors.password = t("login.errors.passwordEmpty");
    } else if (formData.password.length < 8) {
      newErrors.password = t("login.errors.passwordLength");
    }
    setErrors(newErrors);
    setIsFormValid(Object.keys(newErrors).length === 0);
  }, [formData.password, formData.username, t]);

  const validateEmail = (email: string) => {
    const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return re.test(email);
  };

  useEffect(() => {
    validateForm();
  }, [validateForm]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isFormValid) {
      setIsLoading(true);
      try {
        const response = await login(formData.username, formData.password);

        if (response.success) {
          navigate("/migrations");
        } else {
          const errorData = response.content as unknown as LoginErrorData;
          setError(errorData.detail);
          setShowError(true);
        }
      } catch (error) {
        setError(t("login.errors.unexpectedError"));
        setShowError(true);
      } finally {
        setIsLoading(false);
      }
    }
  };
  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "white" }}>
      <Box
        sx={{
          display: { xs: "none", lg: "block" },
          width: "50%",
          backgroundImage: "url(/images/login-background.png)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <Box
        sx={{
          width: { xs: "100%", lg: "50%" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: 3,
          position: "relative", // Añadimos posición relativa para posicionar el selector
        }}
      >
        {/* Posicionamos el selector de idioma en la esquina superior derecha */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1,
          }}
        >
          <LanguageSwitcher />
        </Box>

        <Box sx={{ maxWidth: 400, mx: "auto", width: "100%" }}>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 6 }}>
            <GlobalLogicLogo />
          </Box>

          {isLoading ? (
            <Loader />
          ) : (
            <form onSubmit={handleSubmit}>
              <Collapse in={showError} sx={{ mb: 2 }}>
                <Alert
                  severity="error"
                  sx={{
                    bgcolor: "#FFEBEE",
                    color: "#B41B3A",
                    "& .MuiAlert-icon": {
                      color: "#B41B3A",
                    },
                  }}
                  action={
                    <IconButton aria-label="close" color="inherit" size="small" onClick={() => setShowError(false)}>
                      <CloseIcon fontSize="inherit" />
                    </IconButton>
                  }
                >
                  {error}
                </Alert>
              </Collapse>

              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 400,
                  }}
                >
                  {t("login.username")}
                </Typography>
                <TextField
                  fullWidth
                  name="username"
                  placeholder="example.test"
                  value={formData.username}
                  onChange={handleChange}
                  error={!validateEmail(formData.username) && formData.username !== ""}
                  helperText={
                    !validateEmail(formData.username) && formData.username !== "" ? t("login.errors.invalidEmail") : ""
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      "& fieldset": {
                        borderColor: errors.username ? "#B41B3A" : "#E8E8E8",
                      },
                      "&:hover fieldset": {
                        borderColor: errors.username ? "#B41B3A" : "#E8E8E8",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: errors.username ? "#B41B3A" : "#E8E8E8",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      fontSize: "14px",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#B41B3A",
                      marginLeft: 0,
                    },
                  }}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: "14px",
                    color: "#000000",
                    fontWeight: 400,
                  }}
                >
                  {t("login.password")}
                </Typography>
                <TextField
                  fullWidth
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("login.enterYourPassword")}
                  value={formData.password}
                  onChange={handleChange}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{
                            padding: "4px",
                            color: "#666666",
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      "& fieldset": {
                        borderColor: errors.password ? "#B41B3A" : "#E8E8E8",
                      },
                      "&:hover fieldset": {
                        borderColor: errors.password ? "#B41B3A" : "#E8E8E8",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: errors.password ? "#B41B3A" : "#E8E8E8",
                        borderWidth: "1px",
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "8px 12px",
                      fontSize: "14px",
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#B41B3A",
                      marginLeft: 0,
                    },
                  }}
                />
              </Box>
              <FormControlLabel
                control={
                  <Checkbox
                    name="rememberPassword"
                    checked={formData.rememberPassword}
                    onChange={handleChange}
                    sx={{
                      padding: "0 8px",
                      color: "#999999",
                      "&.Mui-checked": {
                        color: "#999999",
                      },
                      "& .MuiSvgIcon-root": {
                        fontSize: 20,
                      },
                    }}
                  />
                }
                label={t("login.rememberPassword")}
                sx={{
                  mb: 3,
                  "& .MuiFormControlLabel-label": {
                    fontSize: "14px",
                    color: "#666666",
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                disabled={!isFormValid}
                sx={{
                  mb: 3,
                  bgcolor: isFormValid ? "#2064B4" : "#E8E8E8",
                  color: isFormValid ? "#FFFFFF" : "#000000",
                  textTransform: "none",
                  borderRadius: "4px",
                  padding: "8px",
                  fontSize: "14px",
                  fontWeight: 400,
                  "&:hover": {
                    bgcolor: isFormValid ? "#1B4B8F" : "#DADADA",
                  },
                  "&:disabled": {
                    bgcolor: "#E8E8E8",
                    color: "#000000",
                  },
                }}
              >
                {t("login.login")}
              </Button>
              <Typography
                align="center"
                sx={{
                  fontSize: "14px",
                  color: "#666666",
                }}
              >
                {t("login.troubleWithLogin")}{" "}
                <Link
                  href="mailto:support@test.com"
                  sx={{
                    color: "#000000",
                    textDecoration: "none",
                    fontWeight: 700,
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                >
                  support@test.com
                </Link>
              </Typography>
            </form>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
